import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  LabelList,
} from "recharts";
import "./CtgSessions.scss";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import useMediaQuery from "@mui/material/useMediaQuery";
import Loading from "../common/loading";
import NoData from "../common/NoData";
import ChartError from "../common/ChartError";
import { useChartTooltip, ChartHoverTooltip } from "./ChartHoverTooltip";

const makeCustomLabel = (isCompact, showTooltip, hideTooltip) => (props) => {
  const { x, y, width, value } = props;

  if (x === undefined || width === undefined) return null;

  const safeY = y ?? 0;
  const cx = x + width / 2;

  const minHeightThreshold = 1;

  const adjustedY = value <= minHeightThreshold ? safeY - 12 : safeY - 4;

  const valueString = value.toString();
  const maxDigits = 6;
  const isTruncated = valueString.length > maxDigits;
  const displayValue = isTruncated
    ? valueString.slice(0, maxDigits) + "..."
    : value;

  const scale = isCompact ? 0.75 : 1;

  return (
    <g style={{ filter: "drop-shadow(0px 8px 14px rgba(203, 161, 255, 0.5))" }}>
      <path
        d={`
          M ${cx - 32 * scale} ${adjustedY - 28 * scale}
          h ${64 * scale}
          a ${4 * scale} ${4 * scale} 0 0 1 ${4 * scale} ${4 * scale}
          v ${14 * scale}
          a ${4 * scale} ${4 * scale} 0 0 1 ${-4 * scale} ${4 * scale}
          h ${-28 * scale}
          l ${-4 * scale} ${6 * scale}
          l ${-4 * scale} ${-6 * scale}
          h ${-28 * scale}
          a ${4 * scale} ${4 * scale} 0 0 1 ${-4 * scale} ${-4 * scale}
          v ${-14 * scale}
          a ${4 * scale} ${4 * scale} 0 0 1 ${4 * scale} ${-4 * scale}
          Z
        `}
        fill="rgba(203, 161, 255, 1)"
      />
      <text
        x={cx}
        y={adjustedY - 12 * scale}
        fill="rgba(25, 25, 25, 1)"
        fontSize={isCompact ? 8 : 14}
        textAnchor="middle"
        fontFamily="Poppins"
        fontWeight="500"
        style={{ cursor: "pointer" }}
        onMouseEnter={(e) => isTruncated && showTooltip?.(e, valueString)}
        onMouseLeave={() => hideTooltip?.()}
      >
        {displayValue}
      </text>
    </g>
  );
};

const CtgSessions = ({
  data = [],
  dateRangeDisplay,
  isLoading = false,
  hasError = false,
  onBarClick,
}) => {
  const isCompact = useMediaQuery("(max-width:1536px)");
  const { tooltip, showTooltip, hideTooltip } = useChartTooltip();
  const CustomLabel = makeCustomLabel(isCompact, showTooltip, hideTooltip);

  const handleBarCellClick = (entry) => {
    if (!onBarClick || !entry?.id) return;
    onBarClick({
      id: entry.id,
      placeId: entry.placeId,
      value: entry.sessions,
      chartType: "ctg",
    });
  };

  const maxValue = Math.max(...data.map((d) => d.sessions || 0), 0);

  const getNiceStep = (value) => {
    if (value <= 0) return 1;

    const roughStep = value / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;

    let niceNormalized;

    if (normalized <= 1) {
      niceNormalized = 1;
    } else if (normalized <= 1.5) {
      niceNormalized = 1.5;
    } else if (normalized <= 2) {
      niceNormalized = 2;
    } else if (normalized <= 2.5) {
      niceNormalized = 2.5;
    } else if (normalized <= 5) {
      niceNormalized = 5;
    } else {
      niceNormalized = 10;
    }

    return niceNormalized * magnitude;
  };

  const cleanStep = getNiceStep(maxValue);

  const yAxisMax = Math.ceil(maxValue / cleanStep) * cleanStep;

  const yTicks = [];

  for (let value = 0; value <= yAxisMax; value += cleanStep) {
    yTicks.push(value);
  }

  return (
    <div className="ctg-sessions-container">
      <ChartHoverTooltip tooltip={tooltip} />
      {/* Header Row */}
      <div className="ctg-sessions-header">
        <div className="header-left">
          <h3 className="ctg-sessions-title">CTG Sessions</h3>
          <p className="stats-subtitle">By number of Sessions</p>
        </div>

        <div className="header-right">
          {dateRangeDisplay && (
            <div className="date-inline-display">
              <CalendarMonthIcon className="calendar-icon" />
              {dateRangeDisplay}
            </div>
          )}
        </div>
      </div>

      {/* Chart Row */}
      {isLoading ? (
        <div style={{ height: "280px", position: "relative" }}>
          <Loading text="Loading CTG Sessions..." />
        </div>
      ) : hasError ? (
        <ChartError />
      ) : data.length === 0 ? (
        <div style={{ height: "280px", position: "relative" }}>
          <NoData className="stats-no-data" />
        </div>
      ) : (
        <div className="chart-wrapper" style={{ overflowX: "auto" }}>
          <div
            style={{
              minWidth: isCompact
                ? Math.max(data.length * 100, 500)
                : Math.max(data.length * 180, 900),
            }}
          >
            <ResponsiveContainer width="100%" height={isCompact ? 210 : 300}>
              <BarChart
                data={data}
                margin={
                  isCompact
                    ? { top: 40, right: 16, left: 16, bottom: 12 }
                    : { top: 55, right: 30, left: 30, bottom: 20 }
                }
                barSize={isCompact ? 10 : 16}
                barCategoryGap={isCompact ? 18 : 30}
                onMouseDown={(_, e) => e.preventDefault()}
              >
                <CartesianGrid vertical={false} stroke="#EFF1F3" />
                <XAxis
                  dataKey="id"
                  interval={0}
                  axisLine={false}
                  tickLine={false}
                  dy={15}
                  height={isCompact ? 40 : 60}
                  tick={({ x, y, payload }) => {
                    const value = payload.value ?? "";
                    const maxLength = Math.max(
                      10,
                      Math.floor(80 / Math.max(data.length, 1)),
                    );
                    const displayText =
                      value.length > maxLength
                        ? value.slice(0, maxLength) + "..."
                        : value;

                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={16}
                          textAnchor="middle"
                          fill="#8C8C8C"
                          fontSize={isCompact ? 10 : 16}
                          fontFamily="Poppins"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) =>
                            displayText !== value && showTooltip(e, value)
                          }
                          onMouseLeave={hideTooltip}
                        >
                          {displayText}
                        </text>
                      </g>
                    );
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={({ x, y, payload }) => {
                    const value = payload.value ?? 0;
                    const numberPart = Number(value).toString();
                    const fullValue = numberPart;
                    const maxDigits = 6;

                    const displayText =
                      numberPart.length > maxDigits
                        ? `${numberPart.slice(0, maxDigits)}...`
                        : fullValue;

                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          dx={0}
                          dy={4}
                          textAnchor="end"
                          fill="#8C8C8C"
                          fontSize={isCompact ? 10 : 16}
                          fontFamily="Poppins"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) =>
                            displayText !== fullValue &&
                            showTooltip(e, fullValue)
                          }
                          onMouseLeave={hideTooltip}
                        >
                          {displayText}
                        </text>
                      </g>
                    );
                  }}
                  ticks={yTicks}
                  domain={[0, yAxisMax]}
                />

                <Bar
                  dataKey="sessions"
                  radius={[4, 4, 0, 0]}
                  minPointSize={3}
                  activeBar={false}
                  style={{ cursor: onBarClick ? "pointer" : "default" }}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill="#CBA1FF"
                      onClick={() => handleBarCellClick(entry)}
                    />
                  ))}
                  <LabelList dataKey="sessions" content={<CustomLabel />} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default CtgSessions;
